import React, {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import { Drawer, message } from "antd";
import {
  fetchContributorDetailsByProject,
  fetchContributorDetailsByProjectObjective,
  fetchRosterSummaryByProjectObjective,
  fetchRosterCountsByProject,
  transformCountsToSummary,
  fetchProjectObjectivesByProject,
} from "../../services/rosterService";
import RosterView from "./RosterView";
import { useMobileMenu } from "../../context/MobileMenuContext";

/** Align with `NavBar` `<Sider width={260} />`; antd default collapsed width is 80. */
const NAV_SIDEBAR_EXPANDED_PX = 260;
const NAV_SIDEBAR_COLLAPSED_PX = 80;

/**
 * Joins raw project objectives (with cp_status and count per row) into one row per PO
 * with active, production, and percentage.
 */
function joinProjectObjectivesByStatus(objectives) {
  if (!Array.isArray(objectives) || objectives.length === 0) return [];
  const total = objectives.reduce((acc, o) => acc + (o.count || 0), 0);
  const byId = objectives.reduce((acc, objective) => {
    const p = total ? ((objective.count || 0) / total) * 100 : 0;
    if (objective.cp_status === "Production") {
      acc[objective.project_objective_id] = {
        ...(acc[objective.project_objective_id] || objective),
        production: objective.count || "0",
        percentage: (acc[objective.project_objective_id]?.percentage ?? 0) + p,
      };
    } else if (objective.cp_status === "Active") {
      acc[objective.project_objective_id] = {
        ...(acc[objective.project_objective_id] || objective),
        active: objective.count || "0",
        percentage: (acc[objective.project_objective_id]?.percentage ?? 0) + p,
      };
    }
    return acc;
  }, {});
  return Object.values(byId);
}

/**
 * Roster UI + data loading. No forwardRef — safe to render inside the PO Drawer.
 * Parent `RosterOrchestrator` forwardRef wrapper passes `innerRefreshRef` so Refresh still works.
 */
function RosterOrchestratorBody({
  projectId: projectIdProp,
  objectiveId: objectiveIdProp,
  onPOSelect: onPOSelectProp,
  onLastSyncedAtChange,
  innerRefreshRef,
}) {
  const { isOpen: navExpanded } = useMobileMenu();
  const navLeftInsetPx = navExpanded
    ? NAV_SIDEBAR_EXPANDED_PX
    : NAV_SIDEBAR_COLLAPSED_PX;

  const [rosterData, setRosterData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [projectObjectives, setProjectObjectives] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [poTableLoading, setPoTableLoading] = useState(false);
  const [drawerProjectObjective, setDrawerProjectObjective] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const parentHandlesPOSelect = typeof onPOSelectProp === "function";
  const selectedProjectObjectiveIdForView = parentHandlesPOSelect
    ? (objectiveIdProp ?? null)
    : (drawerProjectObjective?.project_objective_id ?? null);

  const loadRosterData = useCallback(
    async (options = {}) => {
      if (!projectIdProp && !objectiveIdProp) {
        setRosterData([]);
        setSummary(null);
        setLastSyncedAt(null);
        return;
      }
      setRosterLoading(true);
      setSummaryLoading(true);
      try {
        if (objectiveIdProp && !projectIdProp) {
          fetchContributorDetailsByProjectObjective(objectiveIdProp, {
            refresh: options.refresh === true,
          })
            .then(({ data, last_synced_at }) => {
              setRosterData(Array.isArray(data) ? data : []);
              setLastSyncedAt(last_synced_at ? new Date(last_synced_at) : null);
            })
            .finally(() => {
              setRosterLoading(false);
            });
          fetchRosterSummaryByProjectObjective(objectiveIdProp, {
            refresh: options.refresh === true,
          })
            .then(({ summary: summaryData, last_synced_at }) => {
              setSummary(summaryData);
              if (last_synced_at != null)
                setLastSyncedAt(new Date(last_synced_at));
            })
            .finally(() => {
              setSummaryLoading(false);
            });
        } else if (projectIdProp) {
          fetchContributorDetailsByProject(projectIdProp, {
            refresh: options.refresh === true,
          })
            .then(({ data, last_synced_at }) => {
              setRosterData(Array.isArray(data) ? data : []);
              setLastSyncedAt(last_synced_at ? new Date(last_synced_at) : null);
            })
            .finally(() => {
              setRosterLoading(false);
            });
          fetchRosterCountsByProject(projectIdProp, {
            refresh: options.refresh === true,
          })
            .then((counts) => {
              setSummary(transformCountsToSummary(counts));
            })
            .finally(() => {
              setSummaryLoading(false);
            });
        }
      } catch (error) {
        console.error("Error loading roster data:", error);
        message.error(error?.message || "Failed to load roster data");
        setRosterData([]);
        setSummary(null);
      }
    },
    [projectIdProp, objectiveIdProp],
  );

  const loadProjectObjectives = useCallback(
    async (options = {}) => {
      if (!projectIdProp) {
        setProjectObjectives([]);
        return;
      }
      setPoTableLoading(true);
      try {
        const data = await fetchProjectObjectivesByProject(projectIdProp, {
          refresh: options.refresh === true,
        });
        const joined = joinProjectObjectivesByStatus(
          Array.isArray(data) ? data : [],
        );
        setProjectObjectives(joined);
      } catch (error) {
        console.error("Error loading project objectives:", error);
        message.error("Failed to load project objectives");
        setProjectObjectives([]);
      } finally {
        setPoTableLoading(false);
      }
    },
    [projectIdProp],
  );

  const handleRefreshRoster = useCallback(() => {
    loadRosterData({ refresh: true });
    if (projectIdProp) {
      loadProjectObjectives({ refresh: true });
    }
  }, [projectIdProp, loadRosterData, loadProjectObjectives]);

  useEffect(() => {
    if (!innerRefreshRef) return;
    innerRefreshRef.current = handleRefreshRoster;
    return () => {
      innerRefreshRef.current = null;
    };
  }, [innerRefreshRef, handleRefreshRoster]);

  useEffect(() => {
    if (typeof onLastSyncedAtChange === "function") {
      onLastSyncedAtChange(lastSyncedAt);
    }
  }, [lastSyncedAt, onLastSyncedAtChange]);

  useEffect(() => {
    loadRosterData();
  }, [loadRosterData]);

  useEffect(() => {
    loadProjectObjectives();
  }, [loadProjectObjectives]);

  const handlePOSelect = (po) => {
    if (parentHandlesPOSelect) {
      onPOSelectProp(po);
      return;
    }
    if (po == null) {
      setIsDrawerOpen(false);
      setDrawerProjectObjective(null);
      return;
    }
    setDrawerProjectObjective(po);
    setIsDrawerOpen(true);
  };

  const showPOName = Boolean(projectIdProp && !objectiveIdProp);
  const showSummary = Boolean(projectIdProp || objectiveIdProp);

  return (
    <>
      <RosterView
        data={rosterData}
        loading={rosterLoading}
        summary={summary}
        showSummary={showSummary}
        showPOName={showPOName}
        projectId={projectIdProp}
        projectObjectives={projectObjectives}
        poTableLoading={poTableLoading}
        selectedProjectObjectiveId={selectedProjectObjectiveIdForView}
        onPOSelect={handlePOSelect}
        summaryLoading={summaryLoading}
      />
      {!parentHandlesPOSelect && projectIdProp && (
        <Drawer
          open={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setDrawerProjectObjective(null);
          }}
          title={drawerProjectObjective?.po_name}
          width={`calc(100vw - ${navLeftInsetPx}px)`}
        >
          <RosterOrchestratorBody
            key={String(drawerProjectObjective?.project_objective_id ?? "po")}
            objectiveId={drawerProjectObjective?.project_objective_id}
          />
        </Drawer>
      )}
    </>
  );
}

/** Exposes refresh for project / roster pages; drawer uses Body only (no nested ref). */
const RosterOrchestrator = forwardRef(function RosterOrchestrator(props, ref) {
  const refreshImplRef = useRef(null);
  useImperativeHandle(
    ref,
    () => ({
      refresh: () => refreshImplRef.current?.(),
    }),
    [],
  );
  return <RosterOrchestratorBody {...props} innerRefreshRef={refreshImplRef} />;
});

export default RosterOrchestrator;
